import "./home.css"
import { Card, Row, Col } from "antd";
import { Check } from "../components/icons"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useScrollReveal } from "../utils/useScrollReveal";

// Helper component for clean feature lists
const FeatureItem = ({ children }) => (
  <p className="flex items-center gap-2 pt-[5px]">
    <span className="flex-shrink-0">
      <Check fill="#000" size={16} />
    </span>
    <span>{children}</span>
  </p>
);

// ADDED setRegisterOpen to props
export default function Home({ isLoggedIn, setLoginOpen, setRegisterOpen, setAccountOpen, setPricingOpen, setFaqOpen }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Scroll reveal hooks for different sections
  const heroSection = useScrollReveal({ threshold: 0.2 });
  const videoSection = useScrollReveal({ threshold: 0.2 });
  const featuresSection = useScrollReveal({ threshold: 0.1 });

  const changeLanguage = () => {
    if (i18n.language && i18n.language === "en") {
      i18n.changeLanguage("fr");
    } else {
      i18n.changeLanguage("en");
    }
  }

  const clickLogin = () => {
    setLoginOpen(true);
  }

  // ADDED clickRegister handler
  const clickRegister = () => {
    // Checks if setRegisterOpen is available before calling it to avoid errors if you forgot to pass it down.
    if (setRegisterOpen) {
        setRegisterOpen(true);
    } else {
        console.warn("setRegisterOpen prop is missing in Home component. Falling back to Login.");
        setLoginOpen(true);
    }
  }


  const baseBtnStyle = "px-10 py-[14px] rounded-md text-[17px] font-medium transition-all duration-200 ease-in-out hover:scale-[1.03] active:scale-[0.97] cursor-pointer";
  const primaryBtnStyle = `${baseBtnStyle} bg-[#1677ff] text-white hover:shadow-md border-none`;
  const secondaryBtnStyle = `${baseBtnStyle} bg-white text-[#333] border border-[#00000069] hover:shadow-md`;

  return (
    <div className="home w-full flex flex-col items-center bg-[#f5f5f7] min-h-[100vh] overflow-x-hidden">

      <div
        className={`container flex flex-col items-center w-full max-w-[1056px] px-[6px] transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
      >
        {/* --- INTRO SECTION --- */}
        <div 
          ref={heroSection.ref}
          className={`intro flex flex-col items-center w-full text-center mt-[30px] md:mt-[50px] scroll-reveal ${heroSection.isVisible ? 'visible' : ''}`}
        >

          <h1 className="mt-[35px] text-gray-900 text-center text-[36px] font-bold md:text-[50px] leading-tight max-w-3xl">
            {t("Engineering standards, powered by AI.")}
          </h1>

          <p className="typography-intro mt-[25px] text-[18px] md:text-[22px] text-gray-600 max-w-[800px] text-center">
            {t("CiviBOT eliminates the friction of navigating dense standards — giving you instant, trustworthy answers so you can focus on engineering, not searching.")}
          </p>

          {/* --- BUTTONS (UPDATED AGAIN) --- */}
          <div className="flex flex-row gap-4 items-center mt-[30px]">
            {isLoggedIn ? (
              /* --- LOGGED IN STATE: [Get Started] [My Account] --- */
              <>
                <button className={`${primaryBtnStyle} btn-shine`} onClick={() => navigate("/c/start")}>
                  {t("home.start_experience")}
                </button>
                <button className={secondaryBtnStyle} onClick={() => setAccountOpen(true)}>
                  {t("home.footer_text1")}
                </button>
              </>
            ) : (
              /* --- LOGGED OUT STATE: [Sign Up] [Login] --- */
              <>
                {/* UPDATED: onClick now calls clickRegister */}
                <button className={`${primaryBtnStyle} btn-shine`} onClick={clickRegister}>
                   {t("home.signup", "Sign Up")}
                </button>
                <button className={secondaryBtnStyle} onClick={clickLogin}>
                  {t("home.footer_text2")}
                </button>
              </>
            )}
          </div>

          <div 
            ref={videoSection.ref}
            className={`scroll-reveal-scale ${videoSection.isVisible ? 'visible' : ''}`}
          >
            <video
              muted
              loop
              autoPlay
              playsInline
              className="mt-[40px] md:mt-[60px] max-w-[1125px] w-full rounded-2xl shadow-2xl border-2 border-[#ccc]"
            >
              <source src="https://storage.googleapis.com/civibot-pdf-storage/videos/CiviBOT%20Demo.mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

{/* --- FEATURES SECTION --- */}
<div 
  ref={featuresSection.ref}
  className={`scroll-reveal ${featuresSection.isVisible ? 'visible' : ''}`}
>
  <Row gutter={[24, 24]} className="w-full mt-[50px] md:mt-[70px]">
    {[
      { title: "home.academic_use", items: ["home.student1", "home.student2", "home.student3"], class: "card-title-1" },
      { title: "home.business_use", items: ["home.b1", "home.b2", "home.b3"], class: "card-title-2" },
      { title: "home.service_promise", items: ["home.service_promise1", "home.service_promise2", "home.service_promise3"], class: "card-title-3" },
    ].map((card, i) => (
      <Col key={i} xs={24} sm={12} md={8}>
        <Card
          title={
            <span className={`${card.class} font-semibold text-lg pb-1`}>
              {t(card.title)}
            </span>
          }
          className="feature-card"
          bordered={false}
        >
          <div className="feature-items">
            {card.items.map((item, j) => (
              <FeatureItem key={j}>{t(item)}</FeatureItem>
            ))}
          </div>
        </Card>
      </Col>
    ))}
  </Row>
</div>

        {/* --- FOOTER SECTION --- */}
        <footer className="w-full mt-auto py-8 cursor-pointer flex flex-col items-center justify-center text-[14px]">
          <div className="flex flex-wrap items-center justify-center text-gray-500">
            {isLoggedIn ? (
              <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={() => setAccountOpen(true)}>{t("home.footer_text1")}</a>
            ) : (
              <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={clickLogin}>{t("home.footer_text2")}</a>
            )}

            <span className="text-gray-400 px-1">|</span>
            <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={() => setFaqOpen(true)}> {t("home.faq")} </a>

            <span className="text-gray-400 px-1">|</span>
            <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={() => window.open("https://www.linkedin.com/company/civibot")}>
              {t("home.footer_text4")}
            </a>
            <span className="text-gray-400 px-1">|</span>
            <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={changeLanguage}> {t("selectLang")} </a>
          </div>
          <div className="footer-desc w-full text-center text-gray-500 text-[14px] mt-3">
            {t("footer")}
          </div>
        </footer>

      </div>
    </div >
  )
}